import * as React from 'react';
import tw, { styled } from 'twin.macro';
import { ChevronRightIcon } from '@heroicons/react/24/outline';
import { useAtomValue, useSetAtom } from 'jotai';

import { UploadProfilePicture } from './upload-profile-picture';
import {
  modalUserInformationVisibleAtom,
  modalEditKey,
  modalCommunicationPrefVisibleAtom,
  isLanguageChangeModalVisibleAtom,
  modalManageNotificationsVisibleAtom,
} from '.';
import type { ModalEditKey } from '.';
import { emailSentFlagAtom, emailVerificationModalVisibleAtom } from './update-user-email-modal';
import { phoneNumberSentFlagAtom, phoneNumberVerificationModalVisibleAtom } from './update-phone-number-modal';
import { validateOtpInputVisibleAtom } from './otp-validation-modal';

export const UserSettingsDataView = ({
  data,
}: {
  data: { label: string; value: string; isEditable?: boolean; key?: ModalEditKey }[];
}) => {
  const showEditModal = useSetAtom(modalUserInformationVisibleAtom);
  const setModalEditKey = useSetAtom(modalEditKey);
  const modalCommunicationPrefVisibleToggler = useSetAtom(modalCommunicationPrefVisibleAtom);
  const emailVerificationModalVisibleToggler = useSetAtom(emailVerificationModalVisibleAtom);
  const isEmailSent = useAtomValue(emailSentFlagAtom);
  const phoneNumberVerificationModalVisibleToggler = useSetAtom(phoneNumberVerificationModalVisibleAtom);
  const phoneNumberSentFlag = useAtomValue(phoneNumberSentFlagAtom);
  const validateOtpInputVisibleToggler = useSetAtom(validateOtpInputVisibleAtom);
  const setIsLanguageChangeModalVisible = useSetAtom(isLanguageChangeModalVisibleAtom);
  const modalManageNotificationsVisibleToggler = useSetAtom(modalManageNotificationsVisibleAtom);

  const handleClick = React.useCallback(
    (key: ModalEditKey, isEditable?: boolean) => {
      if (isEditable) {
        setModalEditKey(key);
        if (key === 'name_and_surname') showEditModal(true);
        if (key === 'communication_pref') modalCommunicationPrefVisibleToggler(true);
        if (key === 'email') emailVerificationModalVisibleToggler(true);
        if (key === 'phone_number') {
          if (phoneNumberSentFlag) validateOtpInputVisibleToggler(true);
          else phoneNumberVerificationModalVisibleToggler(true);
        }
        if (key === 'language') setIsLanguageChangeModalVisible(true);
        if (key === 'manage_notifications') modalManageNotificationsVisibleToggler(true);
      }
    },
    [
      emailVerificationModalVisibleToggler,
      modalCommunicationPrefVisibleToggler,
      phoneNumberSentFlag,
      phoneNumberVerificationModalVisibleToggler,
      setIsLanguageChangeModalVisible,
      setModalEditKey,
      showEditModal,
      validateOtpInputVisibleToggler,
      modalManageNotificationsVisibleToggler,
    ]
  );

  return (
    <DataViewContainer>
      {data.map(({ label, value, isEditable, key }, index) => (
        <DataViewRow
          key={index}
          className={index > 0 ? 'border-t' : ''}
          isEditable={isEditable}
          onClick={() => handleClick(key, isEditable)}
          isFirstIndex={index === 0}
          isLastIndex={data.length - 1 === index}
        >
          {key === 'picture' ? (
            <UploadProfilePicture />
          ) : (
            <>
              <DataRow>
                <DataLabel>{label}</DataLabel>
                <DataValue isEmailSent={isEmailSent && key === 'email'}>{value}</DataValue>
              </DataRow>
              {isEditable && <ChevronRightIcon tw="w-6 h-6 text-neutral-700" />}
            </>
          )}
        </DataViewRow>
      ))}
    </DataViewContainer>
  );
};

const DataViewContainer = tw.ul`rounded-2xl border border-neutral-40 shadow-[0px_2px_4px_0px_rgba(0,0,26,0.05)] flex-col justify-start items-start inline-flex`;
const DataViewRow = styled.li(
  ({
    isEditable,
    isFirstIndex,
    isLastIndex,
  }: {
    isEditable?: boolean;
    isFirstIndex?: boolean;
    isLastIndex?: boolean;
  }) => [
    tw`self-stretch px-4 py-3 border-neutral-40 justify-between items-center inline-flex min-h-[64px]`,
    isEditable && tw`cursor-pointer`,
    isEditable && tw`hover:(duration-200 ease-in-out bg-neutral-30)`,
    isEditable && isFirstIndex && tw`hover:(rounded-t-2xl)`,
    isEditable && isLastIndex && tw`hover:(rounded-b-2xl)`,
  ]
);

const DataLabel = tw.div`text-neutral-200 text-base font-medium`;
const DataValue = styled.div<{
  isEmailSent?: boolean;
}>(({ isEmailSent }) => [tw`text-neutral-700 text-base font-medium`, isEmailSent && tw`text-orange-700`]);
const DataRow = tw.div`grow shrink basis-0 justify-start gap-3 flex flex-col md:(flex-row items-center)`;
